// @ts-nocheck
import Header from '../../components/MainHeader/Header/Header';
import NavMenuBar from '../../components/NavMenu/NavMenuBar/NavMenuBar';
import MainHeaderSkeleton from '../../components/Skeleton/MainHeaderSkeleton/MainHeaderSkeleton';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateUser } from '../../redux/slices/user/user.js';
import { updateTenant } from '../../redux/slices/tenant/tenant.js';
import { CalendarCheck, Books, ChartLineIcon } from '@phosphor-icons/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as PropTypes from 'prop-types';
import './ShellLayout.css';

const navItemsList = [
  {
    id: 3,
    title: 'Trips',
    icon: <CalendarCheck />,
    route: '/trips',
    isVisible: true
  },
  {
    id: 4,
    title: 'Library',
    icon: <Books />,
    route: '/library',
    isVisible: true
  },
  {
    id: 6,
    title: 'Reports',
    icon: <ChartLineIcon />,
    route: '/reports',
    isVisible: false
  }
];

/**
 * This is a reusable main layout component for the shell distribution.
 *
 * @namespace Layouts
 */

const ShellLayout = ({ id, children, headerData, isLoadingContent }) => {
  const [sidebarWidth, setSidebarWidth] = useState(0);
  const sidebarRef = useRef();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const tenant = useSelector((state) => state.tenant);
  const { tripNavigation, enableAIReports } = useFlags();
  const [navItems, setNavItems] = useState(navItemsList);

  const handleResize = useCallback(() => {
    if (sidebarRef.current) {
      setSidebarWidth(sidebarRef.current.offsetWidth);
    }
  }, []);

  useEffect(() => {
    const serializedSessionData = localStorage.getItem('userSession');
    const serializedTenantData = localStorage.getItem('tenantData');
    const sessionData = JSON.parse(serializedSessionData) ?? {};
    const tenantData = JSON.parse(serializedTenantData) ?? {};

    const setupUserSessionData = () => {
      dispatch(updateUser(sessionData));
    };

    const setupUserTenantData = () => {
      dispatch(updateTenant(tenantData));
    };

    setupUserSessionData();
    setupUserTenantData();
  }, [dispatch]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(handleResize);

    if (sidebarRef.current && typeof ResizeObserver === 'function') {
      resizeObserver.observe(sidebarRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [sidebarRef, handleResize]);

  useEffect(() => {
    setNavItems((prev) =>
      prev.map((item) =>
        item.route === '/reports'
          ? {
              ...item,
              isVisible: enableAIReports
            }
          : item
      )
    );
  }, [enableAIReports]);

  return (
    <div id={id} className='shell-wrapper' data-testid='shell-wrapper'>
      <div className='shell-ribbon' data-testid='shell-ribbon' />
      <div
        className='shell-sidebar-wrapper'
        data-testid='shell-sidebar-wrapper'
        ref={sidebarRef}
      >
        <NavMenuBar
          id='navigation-menu'
          navItems={navItems.filter((item) => item.isVisible)}
          data={{
            logo: tenant.logo,
            tenantName: tenant.name,
            username: `${user.firstName} ${user.lastName}`,
            email: user.email
          }}
        />
      </div>
      <main
        className={`shell-content main$$ ${tripNavigation ? 'shell-content--fixed ' : ''}`}
        data-testid='shell-content'
        style={{
          marginLeft: sidebarWidth,
          width: `calc(100vw - ${sidebarWidth}px)`
        }}
      >
        <div
          className={`shell-content-wrapper ${tripNavigation ? 'shell-content-wrapper--fixed' : 'shell-content-wrapper--white'}`}
          data-testid='shell-content-wrapper'
        >
          {headerData &&
            (isLoadingContent ? (
              <MainHeaderSkeleton id='main-header-skeleton' />
            ) : (
              <>
                <Header data={headerData} id='main-header' />
              </>
            ))}
          <div
            className='shell-scrollable-content'
            data-testid='shell-scrollable-content'
          >
            {children}
          </div>
        </div>
      </main>
      {/* <div className='shell-footer-wrapper' data-testid='shell-footer-wrapper'>
        <Footer id='footer' />
      </div> */}
    </div>
  );
};

/**
 * Prop type definitions for ShellLayout.
 */
ShellLayout.propTypes = {
  children: PropTypes.node,
  headerData: PropTypes.object,
  id: PropTypes.string.isRequired,
  isLoadingContent: PropTypes.bool
};

export default ShellLayout;
